"use client";

import { createPortal } from "react-dom";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { ChevronLeft, ChevronRight, KeyRound, LogOut, Paintbrush, RefreshCw, ShieldCheck } from "lucide-react";
import { Button } from "@/components/Button";
import { solicitarCancelarThemeAppearance } from "@/lib/theme-appearance";
import ThemeAppearanceEditor from "@/components/ThemeAppearanceEditor";
import { useExperienciaProduto } from "@/components/ExperienciaProvider";

export const DESIGN_MODE_LAYOUT_EVENT = "bp-design-mode-layout-change";

const LAUNCHER_SELECTOR = "[data-design-mode-launcher]";
const LAUNCHER_ROUTES = new Set(["/configuracoes", "/gestor/configuracoes"]);
const PAINEL_LATERAL_MEDIA_QUERY = "(min-width: 1280px)";

type EtapaAutorizacao = "inativo" | "credenciais" | "codigo";
type RespostaJson = Record<string, unknown>;
type ResultadoStatus = { ativo: boolean; segundosRestantes: number };

function segundosFormatados(segundos: number) {
  const minutos = Math.floor(segundos / 60);
  const restantes = segundos % 60;
  return `${minutos}:${String(restantes).padStart(2, "0")}`;
}

function elementoVisivel(elemento: HTMLElement) {
  if (!elemento.isConnected) return false;

  let atual: HTMLElement | null = elemento;
  while (atual) {
    const estilo = window.getComputedStyle(atual);
    if (estilo.display === "none" || estilo.visibility === "hidden" || estilo.visibility === "collapse" || estilo.opacity === "0") {
      return false;
    }
    atual = atual.parentElement;
  }

  return true;
}

async function lerRespostaJson(resposta: Response): Promise<RespostaJson> {
  const dados = await resposta.json().catch(() => null);
  return dados && typeof dados === "object" && !Array.isArray(dados)
    ? dados as RespostaJson
    : {};
}

function mensagemDeErro(status: number, etapa: "status" | "challenge" | "confirm" | "revoke") {
  if (status === 429) return "Muitas tentativas. Tente novamente mais tarde.";
  if (status === 503) return "Serviço temporariamente indisponível. Tente novamente mais tarde.";
  if (etapa === "status") return "Não foi possível verificar o modo de edição.";
  if (etapa === "confirm") return "Código inválido ou expirado.";
  if (etapa === "revoke") return "Não foi possível encerrar o modo de edição.";
  return "Não foi possível iniciar a autorização.";
}

function ocultaEdicaoAparencia(produto: string, papel: string | null | undefined) {
  if (papel === "PLATFORM_ADMIN" || papel === "PROGRAMADOR") return false;
  return produto === "FOLLOW_ZAP" || (produto === "FOLLOW_PRO" && papel === "GESTOR");
}

// Edição de aparência: sem entrada para FollowZap e FollowPro Gestor (lote 3, etapa 2).
export default function DesignModeAppearance() {
  const { dados, experiencia, carregado } = useExperienciaProduto();
  if (!carregado) return null;
  if (dados && ocultaEdicaoAparencia(experiencia.produto, dados.papel)) return null;
  return <DesignModeAppearanceInterno />;
}

function DesignModeAppearanceInterno() {
  const pathname = usePathname();
  const [designModeAtivo, setDesignModeAtivo] = useState(false);
  const [segundosRestantes, setSegundosRestantes] = useState(0);
  const [etapa, setEtapa] = useState<EtapaAutorizacao>("inativo");
  const [emailAutorizador, setEmailAutorizador] = useState("");
  const [senhaAutorizador, setSenhaAutorizador] = useState("");
  const [codigo, setCodigo] = useState("");
  const [carregandoStatus, setCarregandoStatus] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [encerrando, setEncerrando] = useState(false);
  const [erro, setErro] = useState("");
  const [mensagem, setMensagem] = useState("");
  const [painelAberto, setPainelAberto] = useState(false);
  const [launcherHost, setLauncherHost] = useState<HTMLElement | null>(null);
  const componenteMontado = useRef(false);
  const statusConsulta = useRef(0);
  const statusInicializado = useRef(false);

  const desativarDesignMode = useCallback((mensagemFinal = "") => {
    solicitarCancelarThemeAppearance();
    setDesignModeAtivo(false);
    setSegundosRestantes(0);
    setPainelAberto(false);
    setEtapa("inativo");
    setEmailAutorizador("");
    setSenhaAutorizador("");
    setCodigo("");
    setMensagem(mensagemFinal);
  }, []);

  const consultarStatus = useCallback(async (): Promise<ResultadoStatus | null> => {
    const consultaAtual = ++statusConsulta.current;
    try {
      const resposta = await fetch("/api/design-mode/status", {
        method: "GET",
        credentials: "include",
        cache: "no-store",
      });
      const dados = await lerRespostaJson(resposta);
      if (!componenteMontado.current || consultaAtual !== statusConsulta.current) return null;

      if (!resposta.ok) {
        if (resposta.status === 401 || resposta.status === 403) {
          desativarDesignMode();
          setErro("");
          return { ativo: false, segundosRestantes: 0 };
        }

        setErro(mensagemDeErro(resposta.status, "status"));
        return null;
      }

      if (dados.ativo === true && typeof dados.segundosRestantes === "number" && dados.segundosRestantes > 0) {
        const segundos = Math.ceil(dados.segundosRestantes);
        if (!statusInicializado.current) setPainelAberto(true);
        setDesignModeAtivo(true);
        setSegundosRestantes(segundos);
        setErro("");
        return { ativo: true, segundosRestantes: segundos };
      }

      if (dados.ativo === false || (dados.ativo === true && typeof dados.segundosRestantes === "number" && dados.segundosRestantes <= 0)) {
        desativarDesignMode();
        setErro("");
        return { ativo: false, segundosRestantes: 0 };
      }

      setErro("Não foi possível confirmar temporariamente o status do modo de edição.");
      return null;
    } catch {
      if (!componenteMontado.current || consultaAtual !== statusConsulta.current) return null;
      setErro("Não foi possível confirmar temporariamente o status do modo de edição.");
      return null;
    }
  }, [desativarDesignMode]);

  useEffect(() => {
    componenteMontado.current = true;
    return () => {
      componenteMontado.current = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      solicitarCancelarThemeAppearance();
      window.dispatchEvent(new CustomEvent(DESIGN_MODE_LAYOUT_EVENT, { detail: { aberto: false } }));
    };
  }, []);

  useEffect(() => {
    if (!LAUNCHER_ROUTES.has(pathname)) {
      return;
    }

    const selecionarHostVisivel = () => {
      const host = Array.from(document.querySelectorAll<HTMLElement>(LAUNCHER_SELECTOR)).find(elementoVisivel) ?? null;
      setLauncherHost((atual) => atual === host ? atual : host);
    };

    selecionarHostVisivel();

    const observer = new MutationObserver(() => {
      selecionarHostVisivel();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("resize", selecionarHostVisivel);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", selecionarHostVisivel);
      setLauncherHost(null);
    };
  }, [pathname]);

  useEffect(() => {
    const aberto = designModeAtivo && painelAberto;
    window.dispatchEvent(new CustomEvent(DESIGN_MODE_LAYOUT_EVENT, { detail: { aberto } }));
  }, [designModeAtivo, painelAberto]);

  useEffect(() => {
    if (!designModeAtivo || !painelAberto) return;

    const media = window.matchMedia(PAINEL_LATERAL_MEDIA_QUERY);
    const corpo = document.body;
    let overflowAnterior: string | null = null;

    const sincronizarScroll = () => {
      if (!media.matches) {
        if (overflowAnterior === null) {
          overflowAnterior = corpo.style.overflow;
          corpo.style.overflow = "hidden";
        }
        return;
      }

      if (overflowAnterior !== null) {
        corpo.style.overflow = overflowAnterior;
        overflowAnterior = null;
      }
    };

    const fecharComEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || media.matches) return;
      event.preventDefault();
      setPainelAberto(false);
    };

    sincronizarScroll();
    media.addEventListener("change", sincronizarScroll);
    document.addEventListener("keydown", fecharComEscape);

    return () => {
      media.removeEventListener("change", sincronizarScroll);
      document.removeEventListener("keydown", fecharComEscape);
      if (overflowAnterior !== null) corpo.style.overflow = overflowAnterior;
    };
  }, [designModeAtivo, painelAberto]);

  useEffect(() => {
    let ativo = true;
    const inicio = window.setTimeout(() => {
      void consultarStatus().finally(() => {
        statusInicializado.current = true;
        if (ativo) setCarregandoStatus(false);
      });
    }, 0);

    return () => {
      ativo = false;
      window.clearTimeout(inicio);
    };
  }, [consultarStatus]);

  useEffect(() => {
    if (!designModeAtivo) return;

    const revalidarSeVisivel = () => {
      if (document.visibilityState === "visible") void consultarStatus();
    };
    const revalidarAoFocar = () => {
      if (document.visibilityState === "visible") void consultarStatus();
    };
    const intervalo = window.setInterval(() => void consultarStatus(), 60_000);

    window.addEventListener("focus", revalidarAoFocar);
    document.addEventListener("visibilitychange", revalidarSeVisivel);
    return () => {
      window.clearInterval(intervalo);
      window.removeEventListener("focus", revalidarAoFocar);
      document.removeEventListener("visibilitychange", revalidarSeVisivel);
    };
  }, [designModeAtivo, consultarStatus]);

  useEffect(() => {
    if (!designModeAtivo || segundosRestantes <= 0) {
      if (designModeAtivo && segundosRestantes <= 0) {
        const timer = window.setTimeout(() => desativarDesignMode("O modo de edição expirou."), 0);
        return () => window.clearTimeout(timer);
      }
      return;
    }

    const timer = window.setTimeout(() => {
      setSegundosRestantes((atual) => Math.max(0, atual - 1));
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [designModeAtivo, segundosRestantes, desativarDesignMode]);

  function iniciarAutorizacao() {
    setEtapa("credenciais");
    setErro("");
    setMensagem("");
    setEmailAutorizador("");
    setSenhaAutorizador("");
    setCodigo("");
  }

  async function solicitarChallenge(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!emailAutorizador.trim() || !senhaAutorizador) {
      setErro("Informe o e-mail e a senha da conta autorizadora.");
      return;
    }

    setEnviando(true);
    setErro("");
    try {
      const resposta = await fetch("/api/design-mode/challenge", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: emailAutorizador.trim(), senha: senhaAutorizador }),
      });
      if (!resposta.ok) {
        setErro(mensagemDeErro(resposta.status, "challenge"));
        return;
      }
      setEmailAutorizador("");
      setSenhaAutorizador("");
      setCodigo("");
      setEtapa("codigo");
      setMensagem("Código enviado. Informe o código de seis dígitos para continuar.");
    } catch {
      setErro("Não foi possível iniciar a autorização.");
    } finally {
      setEnviando(false);
    }
  }

  async function confirmarChallenge(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!/^\d{6}$/.test(codigo)) {
      setErro("Informe o código de seis dígitos.");
      return;
    }

    setEnviando(true);
    setErro("");
    try {
      const resposta = await fetch("/api/design-mode/confirm", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ codigo }),
      });
      if (!resposta.ok) {
        setErro(mensagemDeErro(resposta.status, "confirm"));
        return;
      }

      const resultadoStatus = await consultarStatus();
      if (!resultadoStatus?.ativo) {
        if (resultadoStatus) setErro("A autorização não está ativa.");
        return;
      }

      setPainelAberto(true);
      setEtapa("inativo");
      setEmailAutorizador("");
      setSenhaAutorizador("");
      setCodigo("");
      setMensagem("");
    } catch {
      setErro("Não foi possível confirmar a autorização.");
    } finally {
      setEnviando(false);
    }
  }

  async function revogarDesignMode() {
    setEncerrando(true);
    setErro("");
    try {
      const resposta = await fetch("/api/design-mode/revoke", {
        method: "POST",
        credentials: "include",
      });
      if (!resposta.ok) {
        setErro(mensagemDeErro(resposta.status, "revoke"));
        return;
      }
      desativarDesignMode("Modo de edição encerrado.");
    } catch {
      setErro("Não foi possível encerrar o modo de edição.");
    } finally {
      setEncerrando(false);
    }
  }

  const launcher = launcherHost
    ? createPortal(
        carregandoStatus ? (
          <div className="mt-3 flex items-center gap-2 rounded-2xl border border-border bg-white px-4 py-3 text-xs text-muted shadow-sm">
            <RefreshCw className="h-4 w-4 animate-spin" />
            Verificando o modo de edição...
          </div>
        ) : designModeAtivo ? (
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
            <div className="flex items-center gap-2 text-emerald-800">
              <ShieldCheck className="h-5 w-5" />
              <div>
                <p className="text-sm font-semibold">Design Mode ativo</p>
                <p className="text-xs">Tempo restante: {segundosFormatados(segundosRestantes)}</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPainelAberto(true)}
            >
              {painelAberto ? "Painel aberto" : "Abrir painel"}
            </Button>
          </div>
        ) : (
          <div className="mt-3 overflow-hidden rounded-[24px] border border-brand/20 bg-brand/[0.03] shadow-sm">
            <div className="flex items-start gap-3 border-b border-brand/10 px-4 py-4">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-brand/10 text-brand">
                <Paintbrush className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-semibold text-navy">Edição de aparência</h3>
                <p className="mt-1 text-xs leading-relaxed text-muted">
                  Ative o modo temporário com uma conta autorizadora para editar a aparência da Web Beta.
                </p>
              </div>
            </div>

            <div className="space-y-3 p-4">
              {etapa === "inativo" ? (
                <Button
                  variant="primary"
                  size="md"
                  onClick={iniciarAutorizacao}
                  className="w-full"
                >
                  <KeyRound className="h-4 w-4" />
                  Editar aparência
                </Button>
              ) : null}

              {etapa === "credenciais" ? (
                <form className="space-y-3" onSubmit={(event) => void solicitarChallenge(event)}>
                  <div className="rounded-2xl border border-brand/15 bg-white px-3 py-2.5 text-xs leading-relaxed text-muted">
                    Informe separadamente as credenciais da conta PROGRAMADOR ou PLATFORM_ADMIN autorizadora. Elas não serão salvas.
                  </div>
                  <label className="block space-y-1.5">
                    <span className="text-sm font-medium text-navy">E-mail da conta autorizadora</span>
                    <input
                      type="email"
                      value={emailAutorizador}
                      onChange={(event) => setEmailAutorizador(event.target.value)}
                      autoComplete="off"
                      autoFocus
                      className="w-full rounded-xl border border-border bg-white px-3 py-2.5 text-sm text-navy outline-none transition-colors focus:border-brand"
                    />
                  </label>
                  <label className="block space-y-1.5">
                    <span className="text-sm font-medium text-navy">Senha da conta autorizadora</span>
                    <input
                      type="password"
                      value={senhaAutorizador}
                      onChange={(event) => setSenhaAutorizador(event.target.value)}
                      autoComplete="off"
                      className="w-full rounded-xl border border-border bg-white px-3 py-2.5 text-sm text-navy outline-none transition-colors focus:border-brand"
                    />
                  </label>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="md"
                      onClick={() => {
                        setEtapa("inativo");
                        setEmailAutorizador("");
                        setSenhaAutorizador("");
                      }}
                      disabled={enviando}
                      className="flex-1"
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant="primary"
                      size="md"
                      type="submit"
                      loading={enviando}
                      className="flex-1"
                    >
                      {enviando ? <RefreshCw className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                      {enviando ? "Validando..." : "Enviar código"}
                    </Button>
                  </div>
                </form>
              ) : null}

              {etapa === "codigo" ? (
                <form className="space-y-3" onSubmit={(event) => void confirmarChallenge(event)}>
                  <div className="rounded-2xl border border-brand/15 bg-white px-3 py-2.5 text-xs leading-relaxed text-muted">
                    Um código temporário foi enviado ao e-mail da conta autorizadora. O código expira em 10 minutos.
                  </div>
                  <label className="block space-y-1.5">
                    <span className="text-sm font-medium text-navy">Código de seis dígitos</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={codigo}
                      onChange={(event) => setCodigo(event.target.value.replace(/\D/g, "").slice(0, 6))}
                      autoComplete="off"
                      autoFocus
                      maxLength={6}
                      className="w-full rounded-xl border border-border bg-white px-3 py-2.5 text-center font-mono text-lg tracking-[0.35em] text-navy outline-none transition-colors focus:border-brand"
                    />
                  </label>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="md"
                      onClick={() => {
                        setEtapa("inativo");
                        setEmailAutorizador("");
                        setSenhaAutorizador("");
                        setCodigo("");
                        setMensagem("");
                      }}
                      disabled={enviando}
                      className="flex-1"
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant="primary"
                      size="md"
                      type="submit"
                      loading={enviando}
                      className="flex-1"
                    >
                      {enviando ? <RefreshCw className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                      {enviando ? "Confirmando..." : "Ativar Design Mode"}
                    </Button>
                  </div>
                </form>
              ) : null}

              {erro ? <p role="alert" className="text-xs font-semibold text-red-600">{erro}</p> : null}
              {mensagem ? <p className="text-xs font-semibold text-brand">{mensagem}</p> : null}
            </div>
          </div>
        ),
        launcherHost,
      )
    : null;

  const painel = designModeAtivo ? (
    <>
      {painelAberto ? (
        <Button
          variant="ghost"
          size="icon"
          aria-label="Fechar painel de aparência"
          onClick={() => setPainelAberto(false)}
          className="!fixed !inset-0 !z-[170] !h-auto !w-auto !rounded-none !border-0 !bg-[color-mix(in_srgb,var(--fz-button-outline-text)_25%,transparent)] !p-0 xl:!hidden"
        />
      ) : null}

      <aside
        aria-hidden={!painelAberto}
        aria-label="Editor de aparência"
        className={`fixed inset-y-0 right-0 z-[180] flex w-full max-w-[390px] flex-col border-l border-border bg-[var(--surface)] shadow-2xl transition-transform duration-200 xl:w-[380px] xl:max-w-none ${painelAberto ? "translate-x-0" : "pointer-events-none translate-x-full"}`}
        inert={!painelAberto}
      >
        <div className="flex items-start justify-between gap-3 border-b border-border bg-[var(--surface-header)] px-4 py-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Paintbrush className="h-4 w-4 shrink-0 text-brand" />
              <h2 className="truncate text-sm font-bold text-navy">Editor de aparência</h2>
              <span className="rounded-full bg-brand/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-brand">Beta</span>
            </div>
            <p className="mt-1 text-xs text-muted">Design Mode · {segundosFormatados(segundosRestantes)} restantes</p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Recolher editor de aparência"
              onClick={() => setPainelAberto(false)}
              className="!h-8 !w-8"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="danger"
              size="icon"
              aria-label="Encerrar modo de edição"
              onClick={() => void revogarDesignMode()}
              loading={encerrando}
              className="!h-8 !w-8"
            >
              {encerrando ? <RefreshCw className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-b border-emerald-200 bg-emerald-50 px-4 py-2.5 text-emerald-800">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" />
            <span className="text-xs font-semibold">Autorização temporária ativa</span>
          </div>
          <span className="text-xs font-mono">{segundosFormatados(segundosRestantes)}</span>
        </div>

        {erro ? <p role="alert" className="border-b border-red-200 bg-red-50 px-4 py-2 text-xs font-semibold text-red-700">{erro}</p> : null}

        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          <ThemeAppearanceEditor />
        </div>
      </aside>

      {!painelAberto ? (
        <Button
          variant="outline"
          size="sm"
          aria-label="Reabrir editor de aparência"
          onClick={() => setPainelAberto(true)}
          className="!fixed !right-0 !top-1/2 !z-[181] !h-auto !-translate-y-1/2 !rounded-l-xl !rounded-r-none !border-r-0 !px-2 !py-3 shadow-lg"
        >
          <ChevronLeft className="h-4 w-4" />
          <Paintbrush className="h-4 w-4" />
        </Button>
      ) : null}
    </>
  ) : null;

  return <>{launcher}{painel}</>;
}
